import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["canvas", "score", "highScore", "gameOver", "instructions", "leaderboard", "leaderboardTabs", "leaderboardBody"]

  connect() {
    this.ctx = this.canvasTarget.getContext("2d")
    this.canvasWidth = this.canvasTarget.width
    this.canvasHeight = this.canvasTarget.height

    this.gameRunning = false
    this.gameOverState = false
    this.score = 0
    this.isNewHighScore = false

    this.enhancementLabels = { BLAZE: "ENH1", CEDAR: "ENH2", FLINT: "ENH3" }
    this.highScores = {
      base: parseInt(localStorage.getItem("wildleaseTrailBlazeHighScore") || "0", 10),
      BLAZE: parseInt(localStorage.getItem("wildleaseTrailBlazeHighScore_BLAZE") || "0", 10),
      CEDAR: parseInt(localStorage.getItem("wildleaseTrailBlazeHighScore_CEDAR") || "0", 10),
      FLINT: parseInt(localStorage.getItem("wildleaseTrailBlazeHighScore_FLINT") || "0", 10)
    }
    this.updateHighScoreDisplay()

    this.rangerName = localStorage.getItem("wildleaseRangerName") || null
    this.nameOptions = []
    this.namePickerSelected = 0
    this.showingNamePicker = false
    this.scoreSubmitted = false
    this.submittedRank = null

    this.hunterX = 80
    this.hunterY = this.canvasHeight / 2
    this.hunterWidth = 40
    this.hunterHeight = 48
    this.hunterSpeed = 5

    this.keys = { up: false, down: false, right: false }

    this.obstacles = []
    this.obstacleSpeed = 3
    this.obstacleSpawnTimer = 0
    this.obstacleSpawnInterval = 45
    this.difficultyTimer = 0

    this.leafParticles = []

    this.supplies = 0
    this.crateObjects = []
    this.nextCrateScoreThreshold = 500 + Math.floor(Math.random() * 301)

    this.projectiles = []
    this.shootCooldown = 0
    this.shootCooldownMax = 15

    this.invincibilityTimer = 0
    this.invincibilityDuration = 90
    this.shakeTimer = 0
    this.shakeDuration = 12

    this.stumbling = false
    this.stumbleTimer = 0
    this.stumbleDuration = 150
    this.stumbleRotation = 0
    this.stumbleXOffset = 0
    this.stumbleYOffset = 0
    this.stumbleScale = 1
    this.stumbleOpacity = 1

    this.deathCount = 0
    this.activeEnhancement = null
    this.codeInput = ""
    this.codeFlashMessage = ""
    this.codeFlashTimer = 0
    this.codeFlashLoopId = null

    this.hull = 0
    this.hullMax = 2

    this.camoTimer = 0
    this.camoDuration = 240
    this.obstacleSkipCount = 0
    this.camoFlashTimer = 0

    this.campEntrance = null
    this.nextCampScore = 3000 + Math.floor(Math.random() * 501)
    this.inCamp = false
    this.campData = null
    this.campCamera = { x: 0, y: 0 }
    this.campHunterX = 0
    this.campHunterY = 0
    this.campHunterSpeed = 3
    this.campKeys = { left: false, right: false, up: false, down: false }
    this.campDialog = null
    this.nearbyNPC = null
    this.campTransition = null
    this.inventory = [null, null, null, null]
    this.CAMP_WIDTH = 1400
    this.CAMP_HEIGHT = 320

    this._handleKeyDown = this.handleKeyDown.bind(this)
    this._handleKeyUp = this.handleKeyUp.bind(this)
    this._handleClick = this.handleClick.bind(this)

    document.addEventListener("keydown", this._handleKeyDown)
    document.addEventListener("keyup", this._handleKeyUp)
    this.canvasTarget.addEventListener("click", this._handleClick)

    this.animationFrameId = null
    this.drawStartScreen()
  }

  disconnect() {
    document.removeEventListener("keydown", this._handleKeyDown)
    document.removeEventListener("keyup", this._handleKeyUp)
    this.canvasTarget.removeEventListener("click", this._handleClick)
    if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId)
    if (this.codeFlashLoopId) cancelAnimationFrame(this.codeFlashLoopId)
  }

  // --- Input Handling ---

  handleKeyDown(event) {
    if (this.showingNamePicker && this.nameOptions.length > 0) {
      if (event.key === "ArrowUp") { this.namePickerSelected = Math.max(0, this.namePickerSelected - 1); this.drawGameOverScreen(); event.preventDefault(); return }
      if (event.key === "ArrowDown") { this.namePickerSelected = Math.min(this.nameOptions.length - 1, this.namePickerSelected + 1); this.drawGameOverScreen(); event.preventDefault(); return }
      if (event.key === "Enter" || event.key === " ") { this.selectName(); event.preventDefault(); return }
      return
    }

    if (this.gameOverState && this.deathCount >= 1 && !this.stumbling) {
      if (event.key === "Backspace") { this.codeInput = this.codeInput.slice(0, -1); this.drawGameOverScreen(); event.preventDefault(); return }
      if (event.key === "Enter") { if (this.codeInput.length > 0) this.submitCode(); event.preventDefault(); return }
      if (event.key.length === 1 && /[a-zA-Z]/.test(event.key) && this.codeInput.length < 5) { this.codeInput += event.key.toUpperCase(); this.drawGameOverScreen(); event.preventDefault(); return }
    }

    if (!this.gameRunning) return

    if (this.inCamp) {
      if (this.campDialog && this.campDialog.choice) {
        if (event.key === "ArrowLeft" || event.key === "a") { this.campDialog.choice.selected = 0; event.preventDefault(); return }
        if (event.key === "ArrowRight" || event.key === "d") { this.campDialog.choice.selected = 1; event.preventDefault(); return }
      }
      if (event.key === "ArrowLeft" || event.key === "a") { this.campKeys.left = true; event.preventDefault(); return }
      if (event.key === "ArrowRight" || event.key === "d") { this.campKeys.right = true; event.preventDefault(); return }
      if (event.key === "ArrowUp" || event.key === "w") { this.campKeys.up = true; event.preventDefault(); return }
      if (event.key === "ArrowDown" || event.key === "s") { this.campKeys.down = true; event.preventDefault(); return }
      if (event.key === " " || event.key === "Enter") {
        event.preventDefault()
        if (this.campDialog) this.advanceDialog()
        else if (this.nearbyNPC) this.openNPCDialog(this.nearbyNPC)
        return
      }
      return
    }

    if (event.key === "ArrowUp" || event.key === "w") { this.keys.up = true; event.preventDefault() }
    if (event.key === "ArrowDown" || event.key === "s") { this.keys.down = true; event.preventDefault() }
    if (event.key === "ArrowRight" || event.key === "d") { this.keys.right = true; event.preventDefault() }
    if ((event.key === "ArrowLeft" || event.key === "a") && !event.repeat) { this.activateCamo(); event.preventDefault() }
  }

  handleKeyUp(event) {
    if (this.inCamp) {
      if (event.key === "ArrowLeft" || event.key === "a") this.campKeys.left = false
      if (event.key === "ArrowRight" || event.key === "d") this.campKeys.right = false
      if (event.key === "ArrowUp" || event.key === "w") this.campKeys.up = false
      if (event.key === "ArrowDown" || event.key === "s") this.campKeys.down = false
      return
    }

    if (event.key === "ArrowUp" || event.key === "w") this.keys.up = false
    if (event.key === "ArrowDown" || event.key === "s") this.keys.down = false
    if (event.key === "ArrowRight" || event.key === "d") this.keys.right = false

    if (event.key === " ") {
      event.preventDefault()
      if (this.codeInput.length > 0) return
      if (this.showingNamePicker) return
      if (!this.gameRunning && !this.stumbling) this.startGame()
    }
  }

  handleClick(event) {
    const rect = this.canvasTarget.getBoundingClientRect()
    const scaleX = this.canvasWidth / rect.width
    const scaleY = this.canvasHeight / rect.height
    const clickX = (event.clientX - rect.left) * scaleX
    const clickY = (event.clientY - rect.top) * scaleY

    if (this.showingNamePicker && this._namePickerBoxes) {
      for (const box of this._namePickerBoxes) {
        if (clickX >= box.x && clickX <= box.x + box.w && clickY >= box.y && clickY <= box.y + box.h) {
          this.namePickerSelected = box.index
          this.selectName()
          return
        }
      }
      return
    }

    if (this.gameOverState && !this.stumbling && this._leaderboardLinkY) {
      const lbY = this._leaderboardLinkY
      if (clickY >= lbY - 12 && clickY <= lbY + 4 && clickX >= this.canvasWidth / 2 - 60 && clickX <= this.canvasWidth / 2 + 60) {
        this.openLeaderboard()
        return
      }
    }

    if (!this.gameRunning && !this.stumbling && !this.gameOverState) this.startGame()
  }

  // --- Touch Controls ---

  dpadDown(event) { this.setDirection(event.currentTarget.dataset.direction, true) }
  dpadUp(event) { this.setDirection(event.currentTarget.dataset.direction, false) }

  setDirection(dir, pressed) {
    if (this.inCamp) {
      if (this.campDialog && this.campDialog.choice && pressed) {
        if (dir === "left") { this.campDialog.choice.selected = 0; return }
        if (dir === "right") { this.campDialog.choice.selected = 1; return }
      }
      this.campKeys[dir] = pressed
    } else {
      if (dir === "up") this.keys.up = pressed
      if (dir === "down") this.keys.down = pressed
      if (dir === "right" && pressed) this.keys.right = true
      if (dir === "left" && pressed && !this.inCamp) this.activateCamo()
    }
  }

  interact() {
    if (this.showingNamePicker) { this.selectName(); return }
    if (this.gameOverState && !this.stumbling) { this.startGame(); return }
    if (!this.gameRunning && !this.stumbling) { this.startGame(); return }
    if (this.inCamp) {
      if (this.campDialog) this.advanceDialog()
      else if (this.nearbyNPC) this.openNPCDialog(this.nearbyNPC)
    }
  }

  // --- Camo System ---

  activateCamo() {
    if (this.supplies <= 0 || this.camoTimer > 0) return
    this.supplies--
    this.obstacles = []
    this.obstacleSkipCount = 10
    this.camoTimer = this.camoDuration
    this.camoFlashTimer = 20
  }

  // --- Enhancement Codes ---

  submitCode() {
    const validCodes = { BLAZE: true, CEDAR: true, FLINT: true }
    const code = this.codeInput.toUpperCase()
    if (validCodes[code]) {
      this.activeEnhancement = code
      this.codeFlashMessage = `${code} ACTIVE`
      this.codeFlashTimer = 120
      this.updateHighScoreDisplay()
    } else {
      this.codeFlashMessage = "UNKNOWN CODE"
      this.codeFlashTimer = 90
    }
    this.codeInput = ""
    this.drawGameOverScreen()
    this.startCodeFlashLoop()
  }

  startCodeFlashLoop() {
    if (this.codeFlashLoopId) cancelAnimationFrame(this.codeFlashLoopId)
    const loop = () => {
      if (this.codeFlashTimer <= 0 || !this.gameOverState) { this.codeFlashLoopId = null; if (this.gameOverState) this.drawGameOverScreen(); return }
      this.codeFlashTimer--
      this.drawGameOverScreen()
      this.codeFlashLoopId = requestAnimationFrame(loop)
    }
    this.codeFlashLoopId = requestAnimationFrame(loop)
  }

  // --- Game State ---

  startGame() {
    this.gameRunning = true
    this.gameOverState = false
    this.score = 0
    this.isNewHighScore = false
    this.obstacleSpeed = 3
    this.obstacleSpawnInterval = 45
    this.difficultyTimer = 0
    this.obstacleSpawnTimer = 0
    this.obstacles = []
    this.leafParticles = []
    this.hunterY = this.canvasHeight / 2
    this.hunterSpeed = 5
    this.keys = { up: false, down: false, right: false }

    this.stumbling = false
    this.stumbleTimer = 0
    this.stumbleRotation = 0
    this.stumbleXOffset = 0
    this.stumbleYOffset = 0
    this.stumbleScale = 1
    this.stumbleOpacity = 1
    this.supplies = 0
    this.crateObjects = []
    this.nextCrateScoreThreshold = 500 + Math.floor(Math.random() * 301)
    this.projectiles = []
    this.shootCooldown = 0
    this.invincibilityTimer = 0
    this.shakeTimer = 0
    this.hull = 0
    this.camoTimer = 0
    this.obstacleSkipCount = 0
    this.camoFlashTimer = 0
    this.codeInput = ""
    this.codeFlashTimer = 0

    this.campEntrance = null
    this.nextCampScore = 3000 + Math.floor(Math.random() * 501)
    this.inCamp = false
    this.campData = null
    this.campCamera = { x: 0, y: 0 }
    this.campTransition = null
    this.campDialog = null
    this.nearbyNPC = null
    this.inventory = [null, null, null, null]
    this.campKeys = { left: false, right: false, up: false, down: false }

    if (this.codeFlashLoopId) { cancelAnimationFrame(this.codeFlashLoopId); this.codeFlashLoopId = null }

    if (this.activeEnhancement === "BLAZE") this.supplies = 3
    if (this.activeEnhancement === "FLINT") this.hull = this.hullMax

    this.gameOverTarget.classList.add("d-none")
    if (this.hasInstructionsTarget) this.instructionsTarget.classList.remove("d-none")
    this.updateScoreDisplay()

    this.lastTimestamp = null
    this.accumulator = 0
    this.animationFrameId = requestAnimationFrame((t) => this.gameLoop(t))
  }

  startStumbling() {
    this.gameRunning = false
    this.stumbling = true
    this.stumbleTimer = 0
    this.stumbleRotation = 0
    this.stumbleXOffset = 0
    this.stumbleYOffset = 0
    this.stumbleScale = 1
    this.stumbleOpacity = 1
    if (this.animationFrameId) { cancelAnimationFrame(this.animationFrameId); this.animationFrameId = null }
    this.stumbleLoop()
  }

  stumbleLoop() {
    if (!this.stumbling) return
    this.stumbleTimer++
    const t = this.stumbleTimer / this.stumbleDuration

    this.stumbleRotation = t * t * Math.PI * 3
    this.stumbleYOffset = t * t * 120
    this.stumbleXOffset = Math.min(t * 60, 40)
    this.stumbleScale = 1 - t * 0.6
    this.stumbleOpacity = t > 0.6 ? 1 - (t - 0.6) / 0.4 : 1

    for (let i = this.leafParticles.length - 1; i >= 0; i--) {
      this.leafParticles[i].x -= this.leafParticles[i].speed
      if (this.leafParticles[i].x < -5) this.leafParticles.splice(i, 1)
    }
    if (Math.random() < 0.15) {
      this.leafParticles.push({ x: this.canvasWidth + 5, y: Math.random() * this.canvasHeight, speed: this.obstacleSpeed * 0.5 + Math.random(), size: 1 + Math.random() * 2, opacity: 0.2 + Math.random() * 0.3, drift: (Math.random() - 0.5) * 0.8, color: Math.random() < 0.5 ? "green" : "brown" })
    }

    this.drawStumbleFrame()

    if (this.stumbleTimer >= this.stumbleDuration) { this.stumbling = false; this.endGame(); return }
    this.animationFrameId = requestAnimationFrame(() => this.stumbleLoop())
  }

  drawStumbleFrame() {
    const ctx = this.ctx
    ctx.fillStyle = "#1e2f1e"
    ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight)
    this.drawGrass(ctx)
    this.drawLeafParticles(ctx)
    this.obstacles.forEach(obs => this.drawObstacle(ctx, obs))

    const stX = this.hunterX + this.stumbleXOffset
    const stY = this.hunterY + this.stumbleYOffset
    const t = this.stumbleTimer / this.stumbleDuration

    ctx.save()
    ctx.translate(stX, stY)
    ctx.rotate(this.stumbleRotation)
    ctx.scale(this.stumbleScale, this.stumbleScale)
    ctx.globalAlpha = this.stumbleOpacity
    this.drawHunterBody(ctx, 0, 0)
    ctx.restore()

    if (t > 0.1 && Math.random() < 0.4) {
      const dustX = stX + (Math.random() - 0.5) * 20 * this.stumbleScale
      const dustY = stY - Math.random() * 10
      ctx.fillStyle = "rgba(160, 140, 100, 0.5)"
      ctx.beginPath()
      ctx.arc(dustX, dustY, 1.5 + Math.random() * 2, 0, Math.PI * 2)
      ctx.fill()
    }

    ctx.fillStyle = "rgba(255, 255, 255, 0.7)"
    ctx.font = "14px monospace"
    ctx.textAlign = "left"
    ctx.fillText(`Score: ${this.score}`, 10, 25)
    this.drawSupplyHUD(ctx)
    this.drawHullHUD(ctx)
    this.drawInventoryHUD(ctx)
  }

  endGame() {
    this.gameOverState = true
    this.deathCount++
    if (this.animationFrameId) { cancelAnimationFrame(this.animationFrameId); this.animationFrameId = null }

    const scoreKey = this.activeEnhancement || "base"
    const storageKey = this.activeEnhancement ? `wildleaseTrailBlazeHighScore_${this.activeEnhancement}` : "wildleaseTrailBlazeHighScore"
    this.isNewHighScore = this.score > this.highScores[scoreKey]
    if (this.isNewHighScore) { this.highScores[scoreKey] = this.score; localStorage.setItem(storageKey, this.score.toString()); this.updateHighScoreDisplay() }

    this.scoreSubmitted = false
    this.submittedRank = null
    this.showingNamePicker = false

    this.gameOverTarget.classList.remove("d-none")
    if (this.hasInstructionsTarget) this.instructionsTarget.classList.add("d-none")
    this.drawGameOverScreen()

    if (this.rangerName) this.submitScore()
    else this.fetchNameOptions()
  }

  // --- Game Loop ---

  gameLoop(timestamp) {
    if (!this.gameRunning) return
    if (!this.lastTimestamp) this.lastTimestamp = timestamp
    const elapsed = timestamp - this.lastTimestamp
    this.lastTimestamp = timestamp

    this.accumulator = (this.accumulator || 0) + elapsed
    const step = 1000 / 60

    while (this.accumulator >= step) { this.update(); this.accumulator -= step }
    this.draw()
    this.animationFrameId = requestAnimationFrame((t) => this.gameLoop(t))
  }

  update() {
    if (this.campTransition) { this.updateCampTransition(); return }
    if (this.inCamp) { this.updateCamp(); return }

    if (this.invincibilityTimer > 0) this.invincibilityTimer--
    if (this.shakeTimer > 0) this.shakeTimer--
    if (this.shootCooldown > 0) this.shootCooldown--
    if (this.camoTimer > 0) this.camoTimer--
    if (this.camoFlashTimer > 0) this.camoFlashTimer--

    const camoActive = this.camoTimer > 0
    const camoSpeedFactor = camoActive ? 0.5 : 1.0

    // Hunter movement (up/down)
    if (this.keys.up) this.hunterY -= this.hunterSpeed
    if (this.keys.down) this.hunterY += this.hunterSpeed
    if (this.activeEnhancement === "CEDAR") {
      if (this.hunterY < -this.hunterHeight / 2) this.hunterY = this.canvasHeight + this.hunterHeight / 2
      if (this.hunterY > this.canvasHeight + this.hunterHeight / 2) this.hunterY = -this.hunterHeight / 2
    } else {
      this.hunterY = Math.max(this.hunterHeight / 2, Math.min(this.canvasHeight - this.hunterHeight / 2, this.hunterY))
    }

    this.updateShooting()

    // Obstacle spawning
    this.obstacleSpawnTimer++
    if (this.obstacleSpawnTimer >= this.obstacleSpawnInterval) {
      if (this.obstacleSkipCount > 0) this.obstacleSkipCount--
      else this.spawnObstacle()
      this.obstacleSpawnTimer = 0
    }

    // Obstacle movement + collision
    for (let i = this.obstacles.length - 1; i >= 0; i--) {
      this.obstacles[i].x -= this.obstacleSpeed * camoSpeedFactor
      if (this.obstacles[i].hitFlashTimer > 0) this.obstacles[i].hitFlashTimer--
      if (this.obstacles[i].x < -50) { this.obstacles.splice(i, 1); continue }

      if (this.checkCollision(this.obstacles[i])) {
        if (this.invincibilityTimer > 0) continue
        if (this.obstacles[i].type === "thorny_bush" && this.hull > 0) {
          this.hull--; this.invincibilityTimer = this.invincibilityDuration; this.shakeTimer = this.shakeDuration; this.obstacles.splice(i, 1); continue
        }
        if (this.supplies > 0) {
          this.supplies--; this.invincibilityTimer = this.invincibilityDuration; this.shakeTimer = this.shakeDuration; this.obstacles.splice(i, 1); continue
        }
        this.startStumbling(); return
      }
    }

    // Camp entrance
    if (this.campEntrance) {
      this.campEntrance.x -= this.obstacleSpeed * 0.8 * camoSpeedFactor
      this.campEntrance.bobTimer++
      this.campEntrance.lanternTimer++
      if (this.campEntrance.x < -80) {
        this.campEntrance = null
        this.nextCampScore = this.score + 3000 + Math.floor(Math.random() * 501)
      } else if (this.checkCampEntranceCollision()) { this.enterCamp(); return }
    }

    this.updateCrateSpawning()
    this.checkCrateCollection()
    this.updateProjectiles()

    // Leaf particles
    const leafSpawnRate = camoActive ? 0.12 : 0.3
    if (Math.random() < leafSpawnRate) {
      this.leafParticles.push({ x: this.canvasWidth + 5, y: Math.random() * this.canvasHeight, speed: (this.obstacleSpeed * 0.5 + Math.random()) * camoSpeedFactor, size: 1 + Math.random() * 2, opacity: (0.15 + Math.random() * 0.2) * (camoActive ? 0.5 : 1.0), drift: (Math.random() - 0.5) * 0.8, color: Math.random() < 0.5 ? "green" : "brown" })
    }
    for (let i = this.leafParticles.length - 1; i >= 0; i--) {
      this.leafParticles[i].x -= this.leafParticles[i].speed
      this.leafParticles[i].y += this.leafParticles[i].drift
      if (this.leafParticles[i].x < -5) this.leafParticles.splice(i, 1)
    }

    this.score++
    this.updateScoreDisplay()

    if (!this.campEntrance && this.score >= this.nextCampScore) this.spawnCampEntrance()

    this.difficultyTimer++
    if (this.difficultyTimer % 250 === 0) {
      this.obstacleSpeed = Math.min(7, this.obstacleSpeed + 0.35)
      this.obstacleSpawnInterval = Math.max(20, this.obstacleSpawnInterval - 4)
      this.hunterSpeed = Math.min(7, this.hunterSpeed + 0.2)
    }
  }

  // --- Crate System ---

  updateCrateSpawning() {
    if (this.score >= this.nextCrateScoreThreshold && this.supplies < 3) {
      this.spawnCrate()
      this.nextCrateScoreThreshold = this.score + 500 + Math.floor(Math.random() * 301)
    }
    for (let i = this.crateObjects.length - 1; i >= 0; i--) {
      const crate = this.crateObjects[i]
      crate.x -= this.obstacleSpeed * 0.8 * (this.camoTimer > 0 ? 0.5 : 1.0)
      crate.sparkleTimer++
      if (crate.x < -30) this.crateObjects.splice(i, 1)
    }
  }

  spawnCrate() {
    const size = 18
    const y = size + Math.random() * (this.canvasHeight - size * 2)
    this.crateObjects.push({ x: this.canvasWidth + size, y, width: size, height: size, sparkleTimer: 0 })
  }

  checkCrateCollection() {
    const padding = 2
    const hLeft = this.hunterX - this.hunterWidth / 2 + padding
    const hRight = this.hunterX + this.hunterWidth / 2 - padding
    const hTop = this.hunterY - this.hunterHeight / 2 + padding
    const hBottom = this.hunterY + this.hunterHeight / 2 - padding

    for (let i = this.crateObjects.length - 1; i >= 0; i--) {
      const c = this.crateObjects[i]
      if (hLeft < c.x + c.width / 2 && hRight > c.x - c.width / 2 && hTop < c.y + c.height / 2 && hBottom > c.y - c.height / 2) {
        if (this.supplies < 3) {
          this.supplies++
          this.crateObjects.splice(i, 1)
          if (this.activeEnhancement === "FLINT" && this.hull < this.hullMax) this.hull++
        }
      }
    }
  }

  // --- Projectile System ---

  updateShooting() {
    if (this.keys.right && this.supplies >= 3 && this.shootCooldown <= 0) {
      this.spawnProjectile()
      this.shootCooldown = this.shootCooldownMax
    }
  }

  spawnProjectile() {
    this.projectiles.push({ x: this.hunterX + this.hunterWidth / 2 + 5, y: this.hunterY, width: 12, height: 6, speed: 7 })
  }

  updateProjectiles() {
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const proj = this.projectiles[i]
      proj.x += proj.speed
      if (proj.x > this.canvasWidth + 20) { this.projectiles.splice(i, 1); continue }

      let hit = false
      for (let j = this.obstacles.length - 1; j >= 0; j--) {
        if (this.checkProjectileHit(proj, this.obstacles[j])) {
          this.projectiles.splice(i, 1)
          this.obstacles[j].hp--
          if (this.obstacles[j].hp <= 0) { this.obstacles.splice(j, 1); this.score += 50 }
          else this.obstacles[j].hitFlashTimer = 8
          hit = true; break
        }
      }
      if (hit) continue
    }
  }

  checkProjectileHit(proj, obstacle) {
    return proj.x - proj.width / 2 < obstacle.x + obstacle.width / 2 &&
           proj.x + proj.width / 2 > obstacle.x - obstacle.width / 2 &&
           proj.y - proj.height / 2 < obstacle.y + obstacle.height / 2 &&
           proj.y + proj.height / 2 > obstacle.y - obstacle.height / 2
  }

  // --- Obstacles ---

  spawnObstacle() {
    const types = ["boulder", "fallen_tree", "thorny_bush"]
    const type = types[Math.floor(Math.random() * types.length)]
    let width, height
    switch (type) {
      case "boulder": width = 25 + Math.random() * 20; height = 30 + Math.random() * 25; break
      case "fallen_tree": width = 35 + Math.random() * 25; height = 40 + Math.random() * 30; break
      case "thorny_bush": width = 12 + Math.random() * 8; height = 50 + Math.random() * 30; break
      default: width = 35; height = 30
    }
    const y = height / 2 + Math.random() * (this.canvasHeight - height)
    this.obstacles.push({ x: this.canvasWidth + width, y, width, height, type, hp: 2, hitFlashTimer: 0 })
  }

  checkCollision(obstacle) {
    const padding = 4
    return this.hunterX - this.hunterWidth / 2 + padding < obstacle.x + obstacle.width / 2 &&
           this.hunterX + this.hunterWidth / 2 - padding > obstacle.x - obstacle.width / 2 &&
           this.hunterY - this.hunterHeight / 2 + padding < obstacle.y + obstacle.height / 2 &&
           this.hunterY + this.hunterHeight / 2 - padding > obstacle.y - obstacle.height / 2
  }

  // --- Drawing ---

  draw() {
    if (this.campTransition) { this.drawCampTransition(); return }
    if (this.inCamp) { this.drawCamp(); return }

    const ctx = this.ctx
    let shakeX = 0, shakeY = 0
    if (this.shakeTimer > 0) { shakeX = (Math.random() - 0.5) * 8; shakeY = (Math.random() - 0.5) * 8 }

    ctx.save()
    ctx.translate(shakeX, shakeY)

    if (this.camoTimer > 0) {
      const cT = this.camoTimer / this.camoDuration
      ctx.fillStyle = `rgb(${Math.round(20 - 10 * cT)}, ${Math.round(47 + 20 * cT)}, ${Math.round(30 - 5 * cT)})`
    } else {
      ctx.fillStyle = "#1e2f1e"
    }
    ctx.fillRect(-10, -10, this.canvasWidth + 20, this.canvasHeight + 20)

    if (this.camoFlashTimer > 0) {
      ctx.fillStyle = `rgba(100, 180, 100, ${(this.camoFlashTimer / 20) * 0.15})`
      ctx.fillRect(-10, -10, this.canvasWidth + 20, this.canvasHeight + 20)
    }

    // Ground line
    ctx.fillStyle = "rgba(90, 70, 40, 0.15)"
    ctx.fillRect(-10, this.canvasHeight - 8, this.canvasWidth + 20, 10)

    this.drawGrass(ctx)
    this.drawLeafParticles(ctx)
    this.drawCrates(ctx)
    this.drawCampEntrance(ctx)
    this.obstacles.forEach(obs => this.drawObstacle(ctx, obs))
    this.drawHunter(ctx)
    this.drawProjectiles(ctx)

    // HUD
    ctx.fillStyle = "rgba(255, 255, 255, 0.7)"
    ctx.font = "14px monospace"
    ctx.textAlign = "left"
    ctx.fillText(`Score: ${this.score}`, 10, 25)
    this.drawSupplyHUD(ctx)
    this.drawHullHUD(ctx)
    this.drawInventoryHUD(ctx)

    if (this.camoTimer > 0) {
      const cAlpha = Math.min(0.4, this.camoTimer / this.camoDuration + 0.1)
      ctx.fillStyle = `rgba(100, 180, 100, ${cAlpha})`
      ctx.font = "bold 10px monospace"
      ctx.textAlign = "center"
      ctx.fillText("~ CAMO ~", this.canvasWidth / 2, this.canvasHeight - 12)
    }

    if (this.activeEnhancement) {
      ctx.fillStyle = "rgba(255, 255, 255, 0.15)"
      ctx.font = "9px monospace"
      ctx.textAlign = "left"
      ctx.fillText(this.activeEnhancement, 10, this.canvasHeight - 10)
    }

    ctx.restore()
  }

  drawGrass(ctx) {
    const camoActive = this.camoTimer > 0
    const grassAmplitude = camoActive ? 0.8 : 2
    const grassSpacing = camoActive ? 25 : 15
    const grassAlpha = camoActive ? 0.03 : 0.06

    ctx.strokeStyle = `rgba(60, 120, 50, ${grassAlpha})`
    ctx.lineWidth = 1
    const grassOffset = (this.score * 0.5) % grassSpacing

    for (let x = grassOffset; x < this.canvasWidth; x += grassSpacing) {
      ctx.beginPath()
      ctx.moveTo(x, 0)
      for (let y = 0; y < this.canvasHeight; y += 10) {
        ctx.lineTo(x + Math.sin((y + this.score) * 0.05) * grassAmplitude, y)
      }
      ctx.stroke()
    }
  }

  drawLeafParticles(ctx) {
    this.leafParticles.forEach(p => {
      if (p.color === "green") ctx.fillStyle = `rgba(80, 140, 60, ${p.opacity})`
      else ctx.fillStyle = `rgba(140, 100, 50, ${p.opacity})`
      ctx.fillRect(p.x, p.y, p.size * 3, p.size)
    })
  }

  drawHunter(ctx) {
    if (this.invincibilityTimer > 0 && Math.floor(this.invincibilityTimer / 4) % 2 === 0) return
    this.drawHunterBody(ctx, this.hunterX, this.hunterY)
  }

  drawHunterBody(ctx, x, y) {
    // Body - brown jacket
    ctx.fillStyle = "#6d4c41"
    ctx.fillRect(x - 10, y - 12, 20, 24)
    // Head
    ctx.fillStyle = "#e5c9a8"
    ctx.beginPath()
    ctx.arc(x + 2, y - 18, 8, 0, Math.PI * 2)
    ctx.fill()
    // Hat
    ctx.fillStyle = "#4a3520"
    ctx.fillRect(x - 8, y - 26, 22, 4)
    ctx.fillRect(x - 4, y - 32, 14, 8)
    // Legs
    ctx.fillStyle = "#3e423d"
    ctx.fillRect(x - 8, y + 12, 7, 12)
    ctx.fillRect(x + 1, y + 12, 7, 12)
    // Boots
    ctx.fillStyle = "#3b2518"
    ctx.fillRect(x - 9, y + 22, 9, 4)
    ctx.fillRect(x, y + 22, 9, 4)
    // Backpack
    ctx.fillStyle = "#5c3d1e"
    ctx.fillRect(x - 14, y - 8, 6, 14)
    // Rifle on back
    ctx.strokeStyle = "#8b7355"
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.moveTo(x - 12, y - 20)
    ctx.lineTo(x - 6, y + 10)
    ctx.stroke()
  }

  drawObstacle(ctx, obstacle) {
    switch (obstacle.type) {
      case "boulder":
        ctx.fillStyle = "#6b7280"
        ctx.beginPath()
        ctx.moveTo(obstacle.x - obstacle.width * 0.4, obstacle.y + obstacle.height * 0.5)
        ctx.lineTo(obstacle.x - obstacle.width * 0.3, obstacle.y - obstacle.height * 0.3)
        ctx.lineTo(obstacle.x, obstacle.y - obstacle.height * 0.5)
        ctx.lineTo(obstacle.x + obstacle.width * 0.35, obstacle.y - obstacle.height * 0.2)
        ctx.lineTo(obstacle.x + obstacle.width * 0.45, obstacle.y + obstacle.height * 0.5)
        ctx.closePath()
        ctx.fill()
        ctx.strokeStyle = "#4b5563"
        ctx.lineWidth = 1
        ctx.stroke()
        break
      case "fallen_tree":
        ctx.fillStyle = "#5c3d1e"
        ctx.fillRect(obstacle.x - obstacle.width / 2, obstacle.y - 5, obstacle.width, 10)
        ctx.fillStyle = "#4a7a2e"
        ctx.beginPath()
        ctx.arc(obstacle.x - obstacle.width * 0.3, obstacle.y - 10, obstacle.height * 0.25, 0, Math.PI * 2)
        ctx.fill()
        ctx.beginPath()
        ctx.arc(obstacle.x + obstacle.width * 0.1, obstacle.y - 14, obstacle.height * 0.3, 0, Math.PI * 2)
        ctx.fill()
        ctx.fillStyle = "#3d6b22"
        ctx.beginPath()
        ctx.arc(obstacle.x + obstacle.width * 0.3, obstacle.y - 8, obstacle.height * 0.2, 0, Math.PI * 2)
        ctx.fill()
        break
      case "thorny_bush": {
        ctx.fillStyle = "#2d5a1e"
        ctx.beginPath()
        ctx.ellipse(obstacle.x, obstacle.y, obstacle.width / 2, obstacle.height / 2, 0, 0, Math.PI * 2)
        ctx.fill()
        ctx.fillStyle = "#1a3a0e"
        const thorns = 8
        for (let t = 0; t < thorns; t++) {
          const angle = (t / thorns) * Math.PI * 2
          const tx = obstacle.x + Math.cos(angle) * (obstacle.width / 2 + 3)
          const ty = obstacle.y + Math.sin(angle) * (obstacle.height / 2 + 3)
          ctx.beginPath()
          ctx.moveTo(tx, ty)
          ctx.lineTo(tx + Math.cos(angle) * 4, ty + Math.sin(angle) * 4)
          ctx.lineTo(tx + Math.cos(angle + 0.3) * 2, ty + Math.sin(angle + 0.3) * 2)
          ctx.closePath()
          ctx.fill()
        }
        break
      }
    }

    if (obstacle.hitFlashTimer > 0) {
      ctx.fillStyle = `rgba(255, 255, 255, ${obstacle.hitFlashTimer / 8 * 0.6})`
      ctx.fillRect(obstacle.x - obstacle.width / 2, obstacle.y - obstacle.height / 2, obstacle.width, obstacle.height)
    }
    if (obstacle.hp === 1) {
      ctx.fillStyle = "rgba(239, 68, 68, 0.7)"
      ctx.beginPath()
      ctx.arc(obstacle.x, obstacle.y - obstacle.height / 2 - 6, 3, 0, Math.PI * 2)
      ctx.fill()
    }
  }

  drawCrates(ctx) {
    this.crateObjects.forEach(crate => {
      ctx.save()
      ctx.translate(crate.x, crate.y)
      ctx.fillStyle = "#f59e0b"
      ctx.fillRect(-crate.width / 2, -crate.height / 2, crate.width, crate.height)
      ctx.strokeStyle = "#d97706"
      ctx.lineWidth = 1.5
      ctx.strokeRect(-crate.width / 2, -crate.height / 2, crate.width, crate.height)
      ctx.strokeStyle = "#92400e"
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(-crate.width / 2, -crate.height / 2)
      ctx.lineTo(crate.width / 2, crate.height / 2)
      ctx.moveTo(crate.width / 2, -crate.height / 2)
      ctx.lineTo(-crate.width / 2, crate.height / 2)
      ctx.stroke()
      ctx.fillStyle = "#92400e"
      ctx.font = `bold ${crate.width * 0.5}px sans-serif`
      ctx.textAlign = "center"
      ctx.textBaseline = "middle"
      ctx.fillText("W", 0, 1)

      const angle = crate.sparkleTimer * 0.1
      ctx.fillStyle = "rgba(255, 255, 255, 0.8)"
      ctx.beginPath()
      ctx.arc(Math.cos(angle) * (crate.width / 2 - 2), Math.sin(angle) * (crate.height / 2 - 2), 2, 0, Math.PI * 2)
      ctx.fill()
      ctx.restore()
    })
  }

  drawProjectiles(ctx) {
    this.projectiles.forEach(proj => {
      ctx.fillStyle = "rgba(249, 115, 22, 0.3)"
      ctx.beginPath()
      ctx.arc(proj.x, proj.y, proj.width / 2 + 3, 0, Math.PI * 2)
      ctx.fill()
      // Arrow shaft
      ctx.fillStyle = "#8b6914"
      ctx.fillRect(proj.x - proj.width / 2, proj.y - 1.5, proj.width * 0.7, 3)
      // Arrowhead
      ctx.fillStyle = "#9ca3af"
      ctx.beginPath()
      ctx.moveTo(proj.x + proj.width / 2, proj.y)
      ctx.lineTo(proj.x + proj.width / 2 - 5, proj.y - 4)
      ctx.lineTo(proj.x + proj.width / 2 - 5, proj.y + 4)
      ctx.closePath()
      ctx.fill()
      // Feather flights
      ctx.fillStyle = "#ef4444"
      ctx.beginPath()
      ctx.moveTo(proj.x - proj.width / 2, proj.y)
      ctx.lineTo(proj.x - proj.width / 2 + 4, proj.y - 3)
      ctx.lineTo(proj.x - proj.width / 2 + 4, proj.y + 3)
      ctx.closePath()
      ctx.fill()
    })
  }

  drawSupplyHUD(ctx) {
    for (let i = 0; i < 3; i++) {
      const x = this.canvasWidth - 15 - (2 - i) * 22
      const y = 18
      if (i < this.supplies) {
        ctx.fillStyle = "#f59e0b"
        ctx.fillRect(x - 7, y - 7, 14, 14)
        ctx.strokeStyle = "#d97706"
        ctx.lineWidth = 1
        ctx.strokeRect(x - 7, y - 7, 14, 14)
      } else {
        ctx.strokeStyle = "rgba(245, 158, 11, 0.3)"
        ctx.lineWidth = 1
        ctx.strokeRect(x - 7, y - 7, 14, 14)
      }
    }
    if (this.supplies >= 3) {
      ctx.fillStyle = "#f97316"
      ctx.font = "bold 9px monospace"
      ctx.textAlign = "right"
      ctx.fillText("SHOOT [\u2192]", this.canvasWidth - 10, 42)
    }
    if (this.supplies > 0 && this.camoTimer <= 0) {
      const camoY = this.supplies >= 3 ? 53 : 42
      ctx.fillStyle = "rgba(100, 180, 100, 0.5)"
      ctx.font = "bold 9px monospace"
      ctx.textAlign = "right"
      ctx.fillText("CAMO [\u2190]", this.canvasWidth - 10, camoY)
    }
  }

  drawHullHUD(ctx) {
    if (this.activeEnhancement !== "FLINT") return
    let baseY = 42
    if (this.supplies >= 3) baseY = 53
    if (this.supplies > 0 && this.camoTimer <= 0) baseY += 13
    if (this.supplies >= 3 && this.camoTimer > 0) baseY = 53

    for (let i = 0; i < this.hullMax; i++) {
      const x = this.canvasWidth - 15 - (this.hullMax - 1 - i) * 20
      const y = baseY
      if (i < this.hull) {
        ctx.fillStyle = "#6d8c4e"
        ctx.beginPath()
        ctx.moveTo(x, y - 7); ctx.lineTo(x + 6, y - 4); ctx.lineTo(x + 6, y + 2); ctx.lineTo(x, y + 7); ctx.lineTo(x - 6, y + 2); ctx.lineTo(x - 6, y - 4)
        ctx.closePath(); ctx.fill()
        ctx.strokeStyle = "#4a6b30"; ctx.lineWidth = 1; ctx.stroke()
      } else {
        ctx.strokeStyle = "rgba(109, 140, 78, 0.3)"; ctx.lineWidth = 1
        ctx.beginPath()
        ctx.moveTo(x, y - 7); ctx.lineTo(x + 6, y - 4); ctx.lineTo(x + 6, y + 2); ctx.lineTo(x, y + 7); ctx.lineTo(x - 6, y + 2); ctx.lineTo(x - 6, y - 4)
        ctx.closePath(); ctx.stroke()
      }
    }
  }

  // --- Start & Game Over Screens ---

  drawStartScreen() {
    const ctx = this.ctx
    ctx.fillStyle = "#1e2f1e"
    ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight)

    // Draw hunter in center
    this.drawHunterBody(ctx, this.canvasWidth / 2, this.canvasHeight / 2 - 40)

    ctx.fillStyle = "#c8e6c9"
    ctx.font = "bold 20px monospace"
    ctx.textAlign = "center"
    ctx.fillText("trail blaze", this.canvasWidth / 2, this.canvasHeight / 2 + 30)

    ctx.fillStyle = "#9ca3af"
    ctx.font = "13px monospace"
    ctx.fillText("Click or press Space to start", this.canvasWidth / 2, this.canvasHeight / 2 + 55)
    ctx.fillText("Up/Down to move", this.canvasWidth / 2, this.canvasHeight / 2 + 75)
    ctx.fillText("Collect supplies for shields", this.canvasWidth / 2, this.canvasHeight / 2 + 95)
    ctx.fillText("3 supplies = shoot with \u2192", this.canvasWidth / 2, this.canvasHeight / 2 + 115)
    ctx.fillText("\u2190 = use supply to camouflage", this.canvasWidth / 2, this.canvasHeight / 2 + 135)

    let nextY = this.canvasHeight / 2 + 158
    if (this.activeEnhancement) {
      ctx.fillStyle = "#6d8c4e"
      ctx.font = "11px monospace"
      ctx.fillText(`[ ${this.activeEnhancement} ]`, this.canvasWidth / 2, nextY)
      nextY += 22
    }

    ctx.font = "12px monospace"
    const currentKey = this.activeEnhancement || "base"
    if (this.highScores.base > 0) {
      ctx.fillStyle = currentKey === "base" ? "#9ca3af" : "#4b5563"
      ctx.fillText(`High Score: ${this.highScores.base}`, this.canvasWidth / 2, nextY)
      nextY += 16
    }
    for (const [code, label] of Object.entries(this.enhancementLabels)) {
      if (this.highScores[code] > 0) {
        ctx.fillStyle = currentKey === code ? "#9ca3af" : "#4b5563"
        ctx.fillText(`${label}: ${this.highScores[code]}`, this.canvasWidth / 2, nextY)
        nextY += 16
      }
    }
  }

  drawGameOverScreen() {
    const ctx = this.ctx
    ctx.fillStyle = "rgba(0, 0, 0, 0.6)"
    ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight)

    if (this.showingNamePicker) { this.drawNamePicker(ctx); return }

    ctx.fillStyle = "#ef4444"
    ctx.font = "bold 24px monospace"
    ctx.textAlign = "center"
    ctx.fillText("Game Over", this.canvasWidth / 2, this.canvasHeight / 2 - 40)

    ctx.fillStyle = "#e9ecef"
    ctx.font = "16px monospace"
    ctx.fillText(`Score: ${this.score}`, this.canvasWidth / 2, this.canvasHeight / 2 - 10)

    let nextY = this.canvasHeight / 2 + 10

    if (this.supplies > 0) {
      ctx.fillStyle = "#f59e0b"; ctx.font = "13px monospace"
      ctx.fillText(`Supplies: ${this.supplies}`, this.canvasWidth / 2, nextY); nextY += 20
    }

    if (this.isNewHighScore) {
      const label = this.activeEnhancement ? this.enhancementLabels[this.activeEnhancement] : null
      ctx.fillStyle = "#f59e0b"; ctx.font = "13px monospace"
      ctx.fillText(label ? `New ${label} High Score!` : "New High Score!", this.canvasWidth / 2, nextY)
      nextY += 20
    } else { nextY += 5 }

    if (this.scoreSubmitted && this.submittedRank) {
      ctx.fillStyle = "#22c55e"; ctx.font = "11px monospace"
      ctx.fillText(`Rank #${this.submittedRank} as ${this.rangerName}`, this.canvasWidth / 2, nextY); nextY += 18
    } else if (this.rangerName) {
      ctx.fillStyle = "rgba(255, 255, 255, 0.3)"; ctx.font = "10px monospace"
      ctx.fillText(`Ranger ${this.rangerName}`, this.canvasWidth / 2, nextY); nextY += 18
    }

    ctx.fillStyle = "#9ca3af"; ctx.font = "13px monospace"
    ctx.fillText("Press Space to retry", this.canvasWidth / 2, nextY); nextY += 22

    ctx.fillStyle = "#60a5fa"; ctx.font = "11px monospace"
    ctx.fillText("[ Leaderboard ]", this.canvasWidth / 2, nextY)
    this._leaderboardLinkY = nextY; nextY += 22

    if (this.deathCount >= 1) {
      if (this.codeFlashTimer > 0) {
        const isValid = this.codeFlashMessage.includes("ACTIVE")
        ctx.fillStyle = isValid ? "#22c55e" : "#ef4444"
        ctx.font = "bold 12px monospace"
        ctx.fillText(this.codeFlashMessage, this.canvasWidth / 2, nextY + 12)
      } else {
        ctx.fillStyle = "rgba(255, 255, 255, 0.25)"; ctx.font = "10px monospace"
        ctx.fillText("ENHANCEMENT:", this.canvasWidth / 2, nextY + 4)
        const inputText = this.codeInput || ""
        const cursorVisible = Math.floor(Date.now() / 500) % 2 === 0
        ctx.fillStyle = "rgba(255, 255, 255, 0.5)"; ctx.font = "bold 14px monospace"
        ctx.fillText(inputText + (cursorVisible ? "_" : " "), this.canvasWidth / 2, nextY + 22)
      }
      if (this.activeEnhancement && this.codeFlashTimer <= 0) {
        ctx.fillStyle = "rgba(109, 140, 78, 0.5)"; ctx.font = "9px monospace"
        ctx.fillText(`active: ${this.activeEnhancement}`, this.canvasWidth / 2, nextY + 40)
      }
    }
  }

  drawNamePicker(ctx) {
    ctx.fillStyle = "#e2e8f0"; ctx.font = "bold 18px monospace"; ctx.textAlign = "center"
    ctx.fillText("Choose Your Name", this.canvasWidth / 2, this.canvasHeight / 2 - 80)
    ctx.fillStyle = "rgba(255, 255, 255, 0.4)"; ctx.font = "11px monospace"
    ctx.fillText("Select a ranger name:", this.canvasWidth / 2, this.canvasHeight / 2 - 58)

    const boxW = 160, boxH = 28, startY = this.canvasHeight / 2 - 40
    this._namePickerBoxes = []

    this.nameOptions.forEach((name, i) => {
      const x = this.canvasWidth / 2 - boxW / 2, y = startY + i * (boxH + 6)
      const selected = i === this.namePickerSelected
      this._namePickerBoxes.push({ x, y, w: boxW, h: boxH, index: i })
      ctx.fillStyle = selected ? "rgba(96, 165, 250, 0.25)" : "rgba(255, 255, 255, 0.06)"
      ctx.fillRect(x, y, boxW, boxH)
      ctx.strokeStyle = selected ? "#60a5fa" : "rgba(255, 255, 255, 0.15)"
      ctx.lineWidth = selected ? 2 : 1
      ctx.strokeRect(x, y, boxW, boxH)
      ctx.fillStyle = selected ? "#e2e8f0" : "rgba(255, 255, 255, 0.6)"
      ctx.font = `${selected ? "bold " : ""}14px monospace`
      ctx.textAlign = "center"
      ctx.fillText(name, this.canvasWidth / 2, y + boxH / 2 + 5)
    })

    const bottomY = startY + this.nameOptions.length * (boxH + 6) + 12
    ctx.fillStyle = "rgba(255, 255, 255, 0.3)"; ctx.font = "10px monospace"; ctx.textAlign = "center"
    ctx.fillText("Arrow keys + Enter to select", this.canvasWidth / 2, bottomY)
    ctx.fillText("or click a name", this.canvasWidth / 2, bottomY + 14)
  }

  updateScoreDisplay() { this.scoreTarget.textContent = `Score: ${this.score}` }

  updateHighScoreDisplay() {
    const key = this.activeEnhancement || "base"
    const score = this.highScores[key]
    const label = this.activeEnhancement ? this.enhancementLabels[this.activeEnhancement] : "High Score"
    this.highScoreTarget.textContent = `${label}: ${score}`
  }

  // --- Server High Scores ---

  async fetchNameOptions() {
    try {
      const qs = this.rangerName ? `?current_name=${this.rangerName}` : ""
      const resp = await fetch(`/api/trail_high_scores/names${qs}`)
      const data = await resp.json()
      this.nameOptions = data.names || []
      this.namePickerSelected = 0
      this.showingNamePicker = true
      this.drawGameOverScreen()
    } catch { /* silently fail */ }
  }

  selectName() {
    const name = this.nameOptions[this.namePickerSelected]
    if (!name) return
    this.rangerName = name
    localStorage.setItem("wildleaseRangerName", name)
    this.showingNamePicker = false
    this.submitScore()
    this.drawGameOverScreen()
  }

  async submitScore() {
    try {
      const category = this.activeEnhancement || "base"
      const resp = await fetch("/api/trail_high_scores", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ player_name: this.rangerName, score: this.score, category })
      })
      const data = await resp.json()
      if (data.success) { this.scoreSubmitted = true; this.submittedRank = data.rank; this.drawGameOverScreen() }
    } catch { /* silently fail */ }
  }

  async openLeaderboard() {
    if (!this.hasLeaderboardTarget) return
    this.leaderboardTarget.classList.remove("d-none")
    const categories = [
      { key: "base", label: "Base" },
      { key: "BLAZE", label: "ENH1" },
      { key: "CEDAR", label: "ENH2" },
      { key: "FLINT", label: "ENH3" }
    ]
    const tabsEl = this.leaderboardTabsTarget
    tabsEl.innerHTML = ""
    categories.forEach(cat => {
      const btn = document.createElement("button")
      btn.textContent = cat.label
      btn.dataset.category = cat.key
      btn.style.cssText = "flex:1;padding:8px;border:none;background:transparent;color:#94a3b8;font-size:12px;font-family:monospace;cursor:pointer;"
      btn.addEventListener("click", () => this.loadLeaderboardTab(cat.key))
      tabsEl.appendChild(btn)
    })
    this.loadLeaderboardTab("base")
  }

  async loadLeaderboardTab(category) {
    const tabs = this.leaderboardTabsTarget.querySelectorAll("button")
    tabs.forEach(btn => {
      const active = btn.dataset.category === category
      btn.style.borderBottom = active ? "2px solid #60a5fa" : "none"
      btn.style.color = active ? "#e2e8f0" : "#94a3b8"
    })
    const body = this.leaderboardBodyTarget
    body.innerHTML = '<div style="color:#94a3b8;text-align:center;padding:20px;font-size:12px;">Loading...</div>'
    try {
      const resp = await fetch(`/api/trail_high_scores?category=${category}`)
      const scores = await resp.json()
      if (scores.length === 0) { body.innerHTML = '<div style="color:#64748b;text-align:center;padding:20px;font-size:13px;">No scores yet. Be the first!</div>'; return }
      let html = '<table style="width:100%;border-collapse:collapse;font-family:monospace;font-size:12px;"><tr style="color:#64748b;border-bottom:1px solid rgba(255,255,255,0.08);"><td style="padding:4px 8px;">#</td><td style="padding:4px 8px;">Name</td><td style="padding:4px 8px;text-align:right;">Score</td><td style="padding:4px 8px;text-align:right;">Date</td></tr>'
      scores.forEach((s, i) => {
        const isMe = this.rangerName && s.name === this.rangerName
        const rowStyle = isMe ? "color:#60a5fa;background:rgba(96,165,250,0.08);" : "color:#e2e8f0;"
        html += `<tr style="${rowStyle}border-bottom:1px solid rgba(255,255,255,0.04);"><td style="padding:6px 8px;">${i + 1}</td><td style="padding:6px 8px;">${this.escapeHtml(s.name)}</td><td style="padding:6px 8px;text-align:right;">${s.score.toLocaleString()}</td><td style="padding:6px 8px;text-align:right;color:#64748b;">${this.escapeHtml(s.date)}</td></tr>`
      })
      html += '</table>'
      body.innerHTML = html
    } catch { body.innerHTML = '<div style="color:#ef4444;text-align:center;padding:20px;font-size:12px;">Failed to load scores</div>' }
  }

  closeLeaderboard() { if (this.hasLeaderboardTarget) this.leaderboardTarget.classList.add("d-none") }

  escapeHtml(str) { const div = document.createElement("div"); div.textContent = str; return div.innerHTML }

  // --- Trade Items ---

  get TRADE_ITEMS() {
    return {
      tinder:      { id: "tinder",      name: "Tinder Box",   category: "BLAZE", icon: "tinder" },
      torch:       { id: "torch",       name: "Pine Torch",   category: "BLAZE", icon: "torch" },
      flare:       { id: "flare",       name: "Signal Flare", category: "BLAZE", icon: "flare" },
      matches:     { id: "matches",     name: "Matches",      category: "BLAZE", icon: "matches" },
      charcoal:    { id: "charcoal",    name: "Charcoal",     category: "BLAZE", icon: "charcoal" },
      kindling:    { id: "kindling",    name: "Kindling",     category: "BLAZE", icon: "kindling" },
      pinecone:    { id: "pinecone",    name: "Pine Cone",    category: "CEDAR", icon: "pinecone" },
      bark:        { id: "bark",        name: "Cedar Bark",   category: "CEDAR", icon: "bark" },
      acorn:       { id: "acorn",       name: "Golden Acorn", category: "CEDAR", icon: "acorn" },
      leaf_press:  { id: "leaf_press",  name: "Leaf Press",   category: "CEDAR", icon: "leafpress" },
      sapling:     { id: "sapling",     name: "Sapling",      category: "CEDAR", icon: "sapling" },
      resin:       { id: "resin",       name: "Tree Resin",   category: "CEDAR", icon: "resin" },
      arrowhead:   { id: "arrowhead",   name: "Arrowhead",    category: "FLINT", icon: "arrowhead" },
      whetstone:   { id: "whetstone",   name: "Whetstone",    category: "FLINT", icon: "whetstone" },
      flint_shard: { id: "flint_shard", name: "Flint Shard",  category: "FLINT", icon: "flint_shard" },
      geode:       { id: "geode",       name: "Geode",        category: "FLINT", icon: "geode" },
      ore_chunk:   { id: "ore_chunk",   name: "Ore Chunk",    category: "FLINT", icon: "ore_chunk" },
      fossil:      { id: "fossil",      name: "Small Fossil", category: "FLINT", icon: "fossil" }
    }
  }

  // --- Inventory ---

  inventoryFull() { return this.inventory.every(slot => slot !== null) }
  inventoryHas(itemId) { return this.inventory.some(slot => slot && slot.id === itemId) }
  addToInventory(item) { for (let i = 0; i < this.inventory.length; i++) { if (this.inventory[i] === null) { this.inventory[i] = item; return true } } return false }
  removeFromInventory(itemId) { for (let i = 0; i < this.inventory.length; i++) { if (this.inventory[i] && this.inventory[i].id === itemId) { this.inventory[i] = null; return true } } return false }

  // --- Cipher System ---

  encodeItemName(item) {
    const methods = [this.cipherAnagram, this.cipherCaesar, this.cipherReverse, this.cipherFirstLast, this.cipherRiddle]
    return methods[Math.floor(Math.random() * methods.length)].call(this, item)
  }

  cipherAnagram(item) {
    const name = item.name.toUpperCase()
    const letters = name.split("")
    let shuffled
    do { shuffled = [...letters]; for (let i = shuffled.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]] } } while (shuffled.join("") === name && name.length > 1)
    return { text: shuffled.join(""), type: "Scrambled" }
  }

  cipherCaesar(item) {
    const name = item.name.toUpperCase()
    const shift = 3 + Math.floor(Math.random() * 5)
    const encoded = name.split("").map(ch => ch >= "A" && ch <= "Z" ? String.fromCharCode(((ch.charCodeAt(0) - 65 + shift) % 26) + 65) : ch).join("")
    return { text: `${encoded} (+${shift})`, type: "Shifted" }
  }

  cipherReverse(item) { return { text: item.name.toUpperCase().split("").reverse().join(""), type: "Mirrored" } }

  cipherFirstLast(item) {
    const name = item.name.toUpperCase()
    if (name.length <= 2) return { text: name, type: "Partial" }
    return { text: `${name[name.length - 1]}${".".repeat(name.length - 2)}${name[0]}`, type: "Partial" }
  }

  cipherRiddle(item) {
    const riddles = {
      tinder: "A box to start the flame", torch: "Pine-pitch light in darkness tame", flare: "Red signal arcing through the sky",
      matches: "Small sticks that ignite when dry", charcoal: "Black remnants of a fire gone", kindling: "Small sticks to build a fire upon",
      pinecone: "Seeds hidden in a woody scale", bark: "Tree's outer skin, rough to detail", acorn: "An oak tree, just a seed away",
      leaf_press: "Flatten flora, keep decay at bay", sapling: "A tree not yet full-grown", resin: "Sticky sap from timber shown",
      arrowhead: "Chipped stone, sharp and small", whetstone: "Sharpen blades against its wall", flint_shard: "Strike for sparks, a stone so keen",
      geode: "Crystals hidden, rarely seen", ore_chunk: "Raw metal from the mountainside", fossil: "Ancient life in stone resides"
    }
    return { text: riddles[item.id] || "A mysterious wilderness object...", type: "Riddle" }
  }

  pickGiverItem() { const items = Object.values(this.TRADE_ITEMS); return items[Math.floor(Math.random() * items.length)] }

  pickRequesterItem() {
    const items = Object.values(this.TRADE_ITEMS)
    const heldItems = this.inventory.filter(s => s !== null)
    if (heldItems.length > 0 && Math.random() < 0.6) {
      const item = heldItems[Math.floor(Math.random() * heldItems.length)]
      const cipher = this.encodeItemName(item)
      return { item, coded: cipher.text, cipherType: cipher.type }
    }
    const item = items[Math.floor(Math.random() * items.length)]
    const cipher = this.encodeItemName(item)
    return { item, coded: cipher.text, cipherType: cipher.type }
  }

  // --- Camp Entrance ---

  spawnCampEntrance() {
    const side = Math.random() < 0.5 ? "top" : "bottom"
    const width = 70
    const height = 50
    this.campEntrance = {
      side,
      x: this.canvasWidth + width,
      y: side === "top" ? height / 2 + 5 : this.canvasHeight - height / 2 - 5,
      width, height, bobTimer: 0, lanternTimer: 0
    }
  }

  checkCampEntranceCollision() {
    if (!this.campEntrance) return false
    const h = this.campEntrance
    const padding = 4
    return this.hunterX - this.hunterWidth / 2 + padding < h.x + h.width / 2 &&
           this.hunterX + this.hunterWidth / 2 - padding > h.x - h.width / 2 &&
           this.hunterY - this.hunterHeight / 2 + padding < h.y + h.height / 2 &&
           this.hunterY + this.hunterHeight / 2 - padding > h.y - h.height / 2
  }

  drawCampEntrance(ctx) {
    if (!this.campEntrance) return
    const h = this.campEntrance
    const bob = Math.sin(h.bobTimer * 0.04) * 2

    ctx.save()
    ctx.translate(h.x, h.y + bob)

    // Log cabin entrance
    ctx.fillStyle = "#5c3d1e"
    ctx.fillRect(-h.width / 2, -h.height / 2, h.width, 8)
    ctx.fillRect(-h.width / 2, -h.height / 2, 10, h.height)
    ctx.fillRect(h.width / 2 - 10, -h.height / 2, 10, h.height)

    // Planks
    ctx.fillStyle = "#7a5c3a"
    for (let px = -h.width / 2 + 15; px < h.width / 2 - 5; px += 12) {
      ctx.fillRect(px, -h.height / 2 + 2, 3, h.height - 4)
    }

    // Campfire glow
    const flicker = 0.6 + Math.sin(h.lanternTimer * 0.15) * 0.2 + Math.random() * 0.1
    ctx.fillStyle = `rgba(255, 160, 50, ${flicker})`
    ctx.beginPath()
    ctx.arc(0, 4, 5, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillStyle = `rgba(255, 160, 50, ${flicker * 0.12})`
    ctx.beginPath()
    ctx.arc(0, 4, 18, 0, Math.PI * 2)
    ctx.fill()

    ctx.fillStyle = `rgba(255, 200, 80, ${0.4 + Math.sin(h.lanternTimer * 0.05) * 0.15})`
    ctx.font = "bold 7px monospace"
    ctx.textAlign = "center"
    ctx.fillText("CAMP", 0, h.height / 2 + 10)

    ctx.restore()
  }

  // --- Camp Transitions ---

  enterCamp() {
    this.campEntrance = null
    this.campTransition = { type: "entering", timer: 0, duration: 45 }
    this.campData = this.generateCampLayout()
    this.campHunterX = 60
    this.campHunterY = this.CAMP_HEIGHT / 2
    this.campCamera.x = 0
    this.campKeys = { left: false, right: false, up: false, down: false }
  }

  exitCamp() {
    this.campTransition = { type: "exiting", timer: 0, duration: 45 }
    this.campDialog = null
    this.nearbyNPC = null
  }

  updateCampTransition() {
    this.campTransition.timer++
    if (this.campTransition.timer >= this.campTransition.duration) {
      if (this.campTransition.type === "entering") { this.inCamp = true; this.campTransition = null }
      else {
        this.inCamp = false; this.campData = null; this.campTransition = null
        this.nextCampScore = this.score + 3000 + Math.floor(Math.random() * 501)
        this.obstacles = []; this.crateObjects = []
        this.hunterY = this.canvasHeight / 2
      }
    }
  }

  drawCampTransition() {
    const ctx = this.ctx
    const t = this.campTransition.timer / this.campTransition.duration
    ctx.fillStyle = "#1a2a14"
    ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight)
    const blackAlpha = t < 0.5 ? t * 2 : (1 - t) * 2
    ctx.fillStyle = `rgba(0, 0, 0, ${blackAlpha})`
    ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight)
    ctx.fillStyle = `rgba(255, 200, 80, ${Math.sin(t * Math.PI) * 0.6})`
    ctx.font = "bold 14px monospace"
    ctx.textAlign = "center"
    ctx.fillText(this.campTransition.type === "entering" ? "Entering Camp..." : "Hitting the Trail...", this.canvasWidth / 2, this.canvasHeight / 2)
  }

  // --- Camp Scene ---

  generateCampLayout() {
    const camp = { width: this.CAMP_WIDTH, height: this.CAMP_HEIGHT, shelters: [], decorations: [], npcs: [], exitZone: { x: this.CAMP_WIDTH - 40, y: this.CAMP_HEIGHT / 2, width: 50, height: 80 }, leafParticles: [] }

    const shelterSpacing = 130
    for (let x = 200; x < this.CAMP_WIDTH - 200; x += shelterSpacing) {
      camp.shelters.push({ side: "top", x: x + (Math.random() - 0.5) * 30, y: 0, width: 60 + Math.random() * 30, height: 48 + Math.random() * 17 })
      camp.shelters.push({ side: "bottom", x: x + (Math.random() - 0.5) * 30 + shelterSpacing / 2, y: this.CAMP_HEIGHT, width: 60 + Math.random() * 30, height: 48 + Math.random() * 17 })
    }

    camp.shelters.forEach(shelter => {
      const numDecos = 1 + Math.floor(Math.random() * 3)
      for (let i = 0; i < numDecos; i++) {
        const types = ["crate", "barrel", "campfire", "antler"]
        camp.decorations.push({
          type: types[Math.floor(Math.random() * types.length)],
          x: shelter.x + (Math.random() - 0.5) * shelter.width * 0.6,
          y: shelter.side === "top" ? shelter.height + 10 + Math.random() * 15 : shelter.y - shelter.height - 10 - Math.random() * 15,
          size: 6 + Math.random() * 5
        })
      }
      if (Math.random() < 0.5) {
        camp.decorations.push({
          type: "tent",
          x: shelter.x,
          y: shelter.side === "top" ? -5 : this.CAMP_HEIGHT + 5,
          bWidth: 30 + Math.random() * 20,
          bHeight: 40 + Math.random() * 30,
          roofHeight: 10 + Math.random() * 8,
          lit: Math.random() < 0.4,
          side: shelter.side
        })
      }
    })

    const availableShelters = camp.shelters.filter(s => s.width >= 48)
    const shuffled = availableShelters.sort(() => Math.random() - 0.5)
    const numGivers = 1 + Math.floor(Math.random() * 3)
    const numRequesters = 1 + Math.floor(Math.random() * 3)
    let idx = 0

    for (let g = 0; g < numGivers && idx < shuffled.length; g++) {
      const shelter = shuffled[idx++]
      camp.npcs.push({
        role: "giver", x: shelter.x, y: shelter.side === "top" ? shelter.height + 25 : shelter.y - shelter.height - 25,
        width: 12, height: 20, interactionRadius: 55, traded: false, bobTimer: Math.random() * 100,
        item: this.pickGiverItem(), color: "#4ade80", label: "TRAPPER"
      })
    }

    for (let r = 0; r < numRequesters && idx < shuffled.length; r++) {
      const shelter = shuffled[idx++]
      const reqData = this.pickRequesterItem()
      camp.npcs.push({
        role: "requester", x: shelter.x, y: shelter.side === "top" ? shelter.height + 25 : shelter.y - shelter.height - 25,
        width: 12, height: 20, interactionRadius: 55, traded: false, bobTimer: Math.random() * 100,
        item: reqData.item, codedMessage: reqData.coded, cipherType: reqData.cipherType,
        color: "#f59e0b", label: "TRADER"
      })
    }

    if (this.supplies >= 3 && idx < shuffled.length) {
      const shelter = shuffled[idx++]
      camp.npcs.push({
        role: "oracle", x: shelter.x, y: shelter.side === "top" ? shelter.height + 25 : shelter.y - shelter.height - 25,
        width: 12, height: 20, interactionRadius: 55, traded: false, bobTimer: Math.random() * 100,
        color: "#c084fc", label: "ELDER"
      })
    }

    return camp
  }

  updateCamp() {
    if (!this.campDialog) {
      if (this.campKeys.left)  this.campHunterX -= this.campHunterSpeed
      if (this.campKeys.right) this.campHunterX += this.campHunterSpeed
      if (this.campKeys.up)    this.campHunterY -= this.campHunterSpeed
      if (this.campKeys.down)  this.campHunterY += this.campHunterSpeed
      this.campHunterX = Math.max(35, Math.min(this.CAMP_WIDTH - 35, this.campHunterX))
      this.campHunterY = Math.max(20, Math.min(this.CAMP_HEIGHT - 20, this.campHunterY))
    }

    const targetX = this.campHunterX - this.canvasWidth / 2
    const maxCamX = this.CAMP_WIDTH - this.canvasWidth
    this.campCamera.x += (Math.max(0, Math.min(maxCamX, targetX)) - this.campCamera.x) * 0.12

    this.nearbyNPC = null
    if (!this.campDialog && this.campData) {
      for (const npc of this.campData.npcs) {
        const dx = this.campHunterX - npc.x, dy = this.campHunterY - npc.y
        if (Math.sqrt(dx * dx + dy * dy) < npc.interactionRadius) { this.nearbyNPC = npc; break }
      }
    }

    if (!this.campDialog && this.campData) {
      const ez = this.campData.exitZone
      if (this.campHunterX > ez.x - ez.width / 2 && this.campHunterX < ez.x + ez.width / 2 &&
          this.campHunterY > ez.y - ez.height / 2 && this.campHunterY < ez.y + ez.height / 2) {
        this.exitCamp(); return
      }
    }

    if (this.campData && Math.random() < 0.08) {
      this.campData.leafParticles.push({ x: this.campCamera.x + this.canvasWidth + 5, y: this.campCamera.y + Math.random() * this.canvasHeight, speed: 0.2 + Math.random() * 0.3, size: 1 + Math.random() * 1.5, opacity: 0.1 + Math.random() * 0.15, drift: (Math.random() - 0.5) * 0.3 })
    }
    if (this.campData) {
      for (let i = this.campData.leafParticles.length - 1; i >= 0; i--) {
        const p = this.campData.leafParticles[i]
        p.x -= p.speed; p.y += p.drift
        if (p.x < this.campCamera.x - 10) this.campData.leafParticles.splice(i, 1)
      }
    }
  }

  drawCamp() {
    const ctx = this.ctx
    ctx.fillStyle = "#1a2a14"
    ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight)
    ctx.save()
    ctx.translate(-this.campCamera.x, 0)
    this.drawCampGround(ctx)
    this.drawCampShelters(ctx)
    this.drawCampDecorations(ctx)
    this.drawCampNPCs(ctx)
    this.drawCampExitZone(ctx)
    this.drawCampHunter(ctx)
    ctx.restore()
    this.drawCampHUD(ctx)
    if (this.campDialog) this.drawCampDialog(ctx)
  }

  drawCampGround(ctx) {
    if (!this.campData) return
    this.campData.leafParticles.forEach(p => {
      ctx.fillStyle = `rgba(60, 110, 50, ${p.opacity})`
      ctx.fillRect(p.x, p.y, p.size * 2.5, p.size)
    })
    const camLeft = this.campCamera.x, camRight = camLeft + this.canvasWidth
    ctx.strokeStyle = "rgba(60, 100, 50, 0.05)"
    ctx.lineWidth = 1
    for (let x = Math.floor(camLeft / 30) * 30; x < camRight + 30; x += 30) {
      ctx.beginPath(); ctx.moveTo(x, 0)
      for (let y = 0; y < this.CAMP_HEIGHT; y += 10) { ctx.lineTo(x + Math.sin((y + Date.now() * 0.001) * 0.05) * 1.5, y) }
      ctx.stroke()
    }
  }

  drawCampShelters(ctx) {
    if (!this.campData) return
    this.campData.shelters.forEach(shelter => {
      const sy = shelter.side === "top" ? 0 : shelter.y - shelter.height
      ctx.fillStyle = "#5c3d1e"
      ctx.fillRect(shelter.x - shelter.width / 2, sy, shelter.width, shelter.height)
      ctx.strokeStyle = "#4a2e14"; ctx.lineWidth = 1
      for (let lx = shelter.x - shelter.width / 2 + 8; lx < shelter.x + shelter.width / 2; lx += 10) {
        ctx.beginPath(); ctx.moveTo(lx, sy); ctx.lineTo(lx, sy + shelter.height); ctx.stroke()
      }
      ctx.fillStyle = "#7a5c3a"
      if (shelter.side === "top") ctx.fillRect(shelter.x - shelter.width / 2, sy + shelter.height - 3, shelter.width, 3)
      else ctx.fillRect(shelter.x - shelter.width / 2, sy, shelter.width, 3)
    })
  }

  drawCampDecorations(ctx) {
    if (!this.campData) return
    this.campData.decorations.forEach(d => {
      switch (d.type) {
        case "crate":
          ctx.fillStyle = "#8b6914"
          ctx.fillRect(d.x - d.size / 2, d.y - d.size / 2, d.size, d.size)
          ctx.strokeStyle = "#6b5210"; ctx.lineWidth = 1
          ctx.strokeRect(d.x - d.size / 2, d.y - d.size / 2, d.size, d.size)
          ctx.beginPath()
          ctx.moveTo(d.x - d.size / 2, d.y - d.size / 2); ctx.lineTo(d.x + d.size / 2, d.y + d.size / 2)
          ctx.moveTo(d.x + d.size / 2, d.y - d.size / 2); ctx.lineTo(d.x - d.size / 2, d.y + d.size / 2)
          ctx.stroke()
          break
        case "barrel":
          ctx.fillStyle = "#6b4c1e"
          ctx.beginPath(); ctx.ellipse(d.x, d.y, d.size * 0.45, d.size * 0.6, 0, 0, Math.PI * 2); ctx.fill()
          ctx.strokeStyle = "#8b7355"; ctx.lineWidth = 1
          ctx.beginPath(); ctx.ellipse(d.x, d.y - d.size * 0.2, d.size * 0.4, d.size * 0.12, 0, 0, Math.PI * 2); ctx.stroke()
          ctx.beginPath(); ctx.ellipse(d.x, d.y + d.size * 0.2, d.size * 0.4, d.size * 0.12, 0, 0, Math.PI * 2); ctx.stroke()
          break
        case "campfire": {
          const flicker = 0.5 + Math.sin(Date.now() * 0.005 + d.x) * 0.2
          ctx.fillStyle = `rgba(255, 140, 40, ${flicker})`
          ctx.beginPath(); ctx.arc(d.x, d.y, 3, 0, Math.PI * 2); ctx.fill()
          ctx.fillStyle = `rgba(255, 140, 40, ${flicker * 0.12})`
          ctx.beginPath(); ctx.arc(d.x, d.y, 12, 0, Math.PI * 2); ctx.fill()
          break
        }
        case "antler":
          ctx.strokeStyle = "#c8b89a"; ctx.lineWidth = 2
          ctx.beginPath(); ctx.arc(d.x, d.y, d.size * 0.4, 0, Math.PI * 1.7); ctx.stroke()
          ctx.beginPath(); ctx.moveTo(d.x + d.size * 0.3, d.y - d.size * 0.2); ctx.lineTo(d.x + d.size * 0.5, d.y - d.size * 0.5); ctx.stroke()
          break
        case "tent": {
          const tx = d.side === "top" ? d.x - d.bWidth / 2 : d.x - d.bWidth / 2
          const ty = d.side === "top" ? d.y - d.bHeight : d.y
          ctx.fillStyle = "#3a4a30"
          ctx.beginPath()
          ctx.moveTo(tx - 3, ty + d.bHeight)
          ctx.lineTo(tx + d.bWidth / 2, ty)
          ctx.lineTo(tx + d.bWidth + 3, ty + d.bHeight)
          ctx.closePath()
          ctx.fill()
          if (d.lit) {
            ctx.fillStyle = "rgba(255, 200, 100, 0.3)"
            ctx.fillRect(tx + d.bWidth * 0.3, ty + d.bHeight * 0.5, 6, 6)
          }
          break
        }
      }
    })
  }

  drawCampNPCs(ctx) {
    if (!this.campData) return
    this.campData.npcs.forEach(npc => {
      const bob = Math.sin(npc.bobTimer++ * 0.03) * 1.5
      ctx.fillStyle = npc.color
      ctx.fillRect(npc.x - 4, npc.y - 10 + bob, 8, 14)
      ctx.fillStyle = "#e5c9a8"
      ctx.beginPath(); ctx.arc(npc.x, npc.y - 14 + bob, 5, 0, Math.PI * 2); ctx.fill()
      ctx.fillStyle = npc.color; ctx.font = "bold 7px monospace"; ctx.textAlign = "center"
      ctx.fillText(npc.label, npc.x, npc.y - 22 + bob)
      if (this.nearbyNPC === npc && !this.campDialog) {
        const pulse = 0.4 + Math.sin(Date.now() * 0.005) * 0.3
        ctx.fillStyle = `rgba(255, 255, 255, ${pulse})`; ctx.font = "bold 9px monospace"
        ctx.fillText("[SPACE]", npc.x, npc.y + 16 + bob)
      }
      if (npc.traded) { ctx.fillStyle = "rgba(255, 255, 255, 0.3)"; ctx.font = "10px monospace"; ctx.fillText("\u2713", npc.x + 10, npc.y - 14 + bob) }
    })
  }

  drawCampExitZone(ctx) {
    if (!this.campData) return
    const ez = this.campData.exitZone
    ctx.fillStyle = "#5c3d1e"
    ctx.fillRect(ez.x - 10, ez.y - ez.height / 2 - 8, ez.width + 20, 8)
    ctx.fillRect(ez.x - 10, ez.y - ez.height / 2 - 8, 8, ez.height + 16)
    ctx.fillRect(ez.x + ez.width + 2, ez.y - ez.height / 2 - 8, 8, ez.height + 16)
    const pulse = 0.3 + Math.sin(Date.now() * 0.003) * 0.2
    ctx.fillStyle = `rgba(100, 200, 100, ${pulse})`
    ctx.font = "bold 10px monospace"; ctx.textAlign = "center"
    ctx.fillText("EXIT \u2192", ez.x + ez.width / 2, ez.y - ez.height / 2 - 14)
  }

  drawCampHunter(ctx) { this.drawHunterBody(ctx, this.campHunterX, this.campHunterY) }

  drawCampHUD(ctx) {
    ctx.fillStyle = "rgba(255, 255, 255, 0.7)"; ctx.font = "14px monospace"; ctx.textAlign = "left"
    ctx.fillText(`Score: ${this.score}`, 10, 25)
    for (let i = 0; i < 3; i++) {
      const x = this.canvasWidth - 15 - (2 - i) * 22, y = 18
      if (i < this.supplies) { ctx.fillStyle = "#f59e0b"; ctx.fillRect(x - 7, y - 7, 14, 14); ctx.strokeStyle = "#d97706"; ctx.lineWidth = 1; ctx.strokeRect(x - 7, y - 7, 14, 14) }
      else { ctx.strokeStyle = "rgba(245, 158, 11, 0.3)"; ctx.lineWidth = 1; ctx.strokeRect(x - 7, y - 7, 14, 14) }
    }
    ctx.fillStyle = "rgba(255, 200, 80, 0.5)"; ctx.font = "bold 10px monospace"; ctx.textAlign = "center"
    ctx.fillText("~ CAMP ~", this.canvasWidth / 2, this.canvasHeight - 12)
    this.drawInventoryHUD(ctx)
  }

  // --- NPC Dialog ---

  openNPCDialog(npc) {
    if (npc.traded) { this.campDialog = { npc, lines: ["Thank you, ranger. Safe travels."], currentLine: 0, choice: null }; return }

    if (npc.role === "oracle") {
      if (this.supplies < 3) {
        this.campDialog = { npc, lines: ["Return when you carry three supply crates..."], currentLine: 0, choice: null }
      } else {
        this.campDialog = { npc, lines: ["I see your fortune...", "3 supplies for a whisper of power?"], currentLine: 0, choice: { options: ["Trade 3 supplies", "No thanks"], selected: 0, oracleHint: true } }
      }
      return
    }

    if (npc.role === "giver") {
      if (this.inventoryFull()) {
        this.campDialog = { npc, lines: ["Howdy! I have goods for transport...", "But your pack is full! Come back with room."], currentLine: 0, choice: null }
      } else {
        this.campDialog = { npc, lines: ["Howdy! I need this delivered:", `>> ${npc.item.name} <<`, "Will you take it?"], currentLine: 0, choice: { options: ["Accept", "Decline"], selected: 0 } }
      }
    } else {
      const hasItem = this.inventoryHas(npc.item.id)
      if (hasItem) {
        this.campDialog = { npc, lines: [`I'm looking for... ${npc.codedMessage}`, `(${npc.cipherType})`, "You have it! Choose your payment:"], currentLine: 0, choice: { options: ["+500 pts", "+1 Supply"], selected: 0, rewardChoice: true } }
      } else {
        this.campDialog = { npc, lines: [`I'm looking for... ${npc.codedMessage}`, `(${npc.cipherType})`, "Hmm, you don't have it. Safe travels."], currentLine: 0, choice: null }
      }
    }
  }

  advanceDialog() {
    const d = this.campDialog
    if (!d) return
    if (d.choice && d.currentLine === d.lines.length - 1) { this.resolveDialogChoice(d); return }
    d.currentLine++
    if (d.currentLine >= d.lines.length) { this.campDialog = null; return }
  }

  resolveDialogChoice(dialog) {
    if (dialog.npc.role === "giver") {
      if (dialog.choice.selected === 0) { this.addToInventory(dialog.npc.item); dialog.npc.traded = true }
    } else if (dialog.choice.oracleHint) {
      if (dialog.choice.selected === 0 && this.supplies >= 3) {
        this.supplies -= 3
        dialog.npc.traded = true
        this.campDialog = { npc: dialog.npc, lines: [this.getStrongHint()], currentLine: 0, choice: null }
        return
      }
      dialog.npc.traded = true
    } else if (dialog.choice.rewardChoice) {
      this.removeFromInventory(dialog.npc.item.id)
      if (dialog.choice.selected === 0) { this.score += 500; this.updateScoreDisplay() }
      else this.supplies = Math.min(3, this.supplies + 1)
      dialog.npc.traded = true
    }
    this.campDialog = null
  }

  getStrongHint() {
    const hints = {
      BLAZE: ["Five letters. Starts with B. Think fire.", "A trail of fire, five letters.", "_ L A Z E \u2014 fill in the blank."],
      CEDAR: ["Five letters. Starts with C. Think trees.", "A tall red-barked tree, five letters.", "_ E D A R \u2014 fill in the blank."],
      FLINT: ["Five letters. Starts with F. Think stone.", "Strike this rock to make sparks, five letters.", "_ L I N T \u2014 fill in the blank."]
    }
    const codes = Object.keys(hints)
    const code = codes[Math.floor(Math.random() * codes.length)]
    return hints[code][Math.floor(Math.random() * hints[code].length)]
  }

  drawCampDialog(ctx) {
    const d = this.campDialog
    if (!d) return
    const boxH = 80, boxY = this.canvasHeight - boxH - 10, boxX = 10, boxW = this.canvasWidth - 20

    ctx.fillStyle = "rgba(10, 20, 14, 0.88)"
    ctx.fillRect(boxX, boxY, boxW, boxH)
    ctx.strokeStyle = d.npc.color; ctx.lineWidth = 1.5
    ctx.strokeRect(boxX, boxY, boxW, boxH)

    ctx.fillStyle = d.npc.color; ctx.font = "bold 9px monospace"; ctx.textAlign = "left"
    ctx.fillText(d.npc.label, boxX + 8, boxY + 14)

    ctx.fillStyle = "#e9ecef"; ctx.font = "11px monospace"; ctx.textAlign = "left"
    this.drawWrappedText(ctx, d.lines[d.currentLine], boxX + 8, boxY + 30, boxW - 16, 14)

    if (d.choice && d.currentLine === d.lines.length - 1) {
      const opts = d.choice.options
      for (let i = 0; i < opts.length; i++) {
        const optX = boxX + 100 + i * 140, optY = boxY + boxH - 14
        const selected = d.choice.selected === i
        ctx.fillStyle = selected ? d.npc.color : "rgba(255,255,255,0.3)"
        ctx.font = selected ? "bold 11px monospace" : "11px monospace"
        ctx.textAlign = "center"
        ctx.fillText(selected ? `> ${opts[i]} <` : opts[i], optX, optY)
      }
    } else {
      const blink = Math.floor(Date.now() / 400) % 2 === 0
      if (blink) { ctx.fillStyle = "rgba(255,255,255,0.4)"; ctx.font = "9px monospace"; ctx.textAlign = "right"; ctx.fillText("[SPACE]", boxX + boxW - 8, boxY + boxH - 8) }
    }
  }

  drawWrappedText(ctx, text, x, y, maxWidth, lineHeight) {
    const words = text.split(" ")
    let currentLine = "", currentY = y
    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word
      if (ctx.measureText(testLine).width > maxWidth && currentLine) { ctx.fillText(currentLine, x, currentY); currentLine = word; currentY += lineHeight }
      else currentLine = testLine
    }
    if (currentLine) ctx.fillText(currentLine, x, currentY)
  }

  // --- Inventory HUD ---

  drawInventoryHUD(ctx) {
    if (!this.inventory.some(s => s) && !this.inCamp) return
    const slotSize = 18, slotPadding = 2, baseX = 10, baseY = this.canvasHeight - 32
    ctx.fillStyle = "rgba(255, 255, 255, 0.15)"; ctx.font = "7px monospace"; ctx.textAlign = "left"
    ctx.fillText("PACK", baseX, baseY - 3)

    for (let i = 0; i < 4; i++) {
      const x = baseX + i * (slotSize + slotPadding), y = baseY
      ctx.strokeStyle = "rgba(255, 255, 255, 0.25)"; ctx.lineWidth = 1; ctx.strokeRect(x, y, slotSize, slotSize)
      const item = this.inventory[i]
      if (item) {
        ctx.fillStyle = "rgba(255, 255, 255, 0.08)"; ctx.fillRect(x, y, slotSize, slotSize)
        this.drawItemIcon(ctx, item, x + slotSize / 2, y + slotSize / 2, slotSize * 0.65)
      }
    }
  }

  drawItemIcon(ctx, item, cx, cy, size) {
    const s = size / 2
    ctx.save()
    ctx.translate(cx, cy)
    switch (item.icon) {
      case "tinder":
        ctx.fillStyle = "#8b6914"; ctx.fillRect(-s * 0.5, -s * 0.5, s, s)
        ctx.fillStyle = "#f97316"; ctx.beginPath(); ctx.arc(0, -s * 0.3, s * 0.25, 0, Math.PI * 2); ctx.fill()
        break
      case "torch":
        ctx.fillStyle = "#5c3d1e"; ctx.fillRect(-s * 0.15, -s * 0.3, s * 0.3, s * 0.8)
        ctx.fillStyle = "#f97316"; ctx.beginPath(); ctx.arc(0, -s * 0.5, s * 0.3, 0, Math.PI * 2); ctx.fill()
        break
      case "flare":
        ctx.fillStyle = "#ef4444"; ctx.fillRect(-s * 0.2, -s * 0.7, s * 0.4, s * 1.4)
        ctx.fillStyle = "#fbbf24"; ctx.beginPath(); ctx.arc(0, -s * 0.7, s * 0.25, 0, Math.PI * 2); ctx.fill()
        break
      case "matches":
        ctx.fillStyle = "#8b6914"; ctx.fillRect(-s * 0.4, -s * 0.1, s * 0.8, s * 0.6)
        ctx.fillStyle = "#ef4444"; ctx.fillRect(-s * 0.1, -s * 0.6, s * 0.15, s * 0.5)
        break
      case "charcoal":
        ctx.fillStyle = "#1a1a1a"; ctx.beginPath(); ctx.arc(0, 0, s * 0.7, 0, Math.PI * 2); ctx.fill()
        ctx.fillStyle = "#333"; ctx.beginPath(); ctx.arc(-s * 0.2, -s * 0.1, s * 0.2, 0, Math.PI * 2); ctx.fill()
        break
      case "kindling":
        ctx.strokeStyle = "#8b6914"; ctx.lineWidth = 1.5
        ctx.beginPath(); ctx.moveTo(-s * 0.4, s * 0.4); ctx.lineTo(s * 0.2, -s * 0.6); ctx.stroke()
        ctx.beginPath(); ctx.moveTo(-s * 0.1, s * 0.5); ctx.lineTo(s * 0.5, -s * 0.3); ctx.stroke()
        break
      case "pinecone":
        ctx.fillStyle = "#5c3d1e"; ctx.beginPath(); ctx.ellipse(0, 0, s * 0.5, s * 0.7, 0, 0, Math.PI * 2); ctx.fill()
        ctx.strokeStyle = "#4a2e14"; ctx.lineWidth = 0.5
        for (let a = 0; a < 6; a++) { ctx.beginPath(); ctx.arc(Math.cos(a) * s * 0.3, Math.sin(a) * s * 0.4, s * 0.15, 0, Math.PI); ctx.stroke() }
        break
      case "bark":
        ctx.fillStyle = "#6b4c1e"; ctx.fillRect(-s * 0.6, -s * 0.4, s * 1.2, s * 0.8)
        ctx.strokeStyle = "#5c3d1e"; ctx.lineWidth = 1
        ctx.beginPath(); ctx.moveTo(-s * 0.4, -s * 0.4); ctx.lineTo(-s * 0.4, s * 0.4); ctx.stroke()
        break
      case "acorn":
        ctx.fillStyle = "#8b6914"; ctx.fillRect(-s * 0.3, -s * 0.7, s * 0.6, s * 0.35)
        ctx.fillStyle = "#fbbf24"; ctx.beginPath(); ctx.ellipse(0, s * 0.1, s * 0.4, s * 0.5, 0, 0, Math.PI * 2); ctx.fill()
        break
      case "leafpress":
        ctx.fillStyle = "#4a7a2e"; ctx.fillRect(-s * 0.6, -s * 0.5, s * 1.2, s)
        ctx.strokeStyle = "#3d6b22"; ctx.lineWidth = 1; ctx.strokeRect(-s * 0.6, -s * 0.5, s * 1.2, s)
        break
      case "sapling":
        ctx.fillStyle = "#5c3d1e"; ctx.fillRect(-s * 0.1, -s * 0.2, s * 0.2, s * 0.8)
        ctx.fillStyle = "#4a7a2e"; ctx.beginPath(); ctx.arc(0, -s * 0.4, s * 0.4, 0, Math.PI * 2); ctx.fill()
        break
      case "resin":
        ctx.fillStyle = "#f59e0b"; ctx.beginPath(); ctx.ellipse(0, 0, s * 0.5, s * 0.6, 0, 0, Math.PI * 2); ctx.fill()
        ctx.fillStyle = "#d97706"; ctx.beginPath(); ctx.arc(s * 0.1, s * 0.1, s * 0.2, 0, Math.PI * 2); ctx.fill()
        break
      case "arrowhead":
        ctx.fillStyle = "#6b7280"; ctx.beginPath()
        ctx.moveTo(0, -s * 0.8); ctx.lineTo(-s * 0.5, s * 0.5); ctx.lineTo(0, s * 0.3); ctx.lineTo(s * 0.5, s * 0.5)
        ctx.closePath(); ctx.fill()
        break
      case "whetstone":
        ctx.fillStyle = "#9ca3af"; ctx.fillRect(-s * 0.6, -s * 0.3, s * 1.2, s * 0.6)
        ctx.fillStyle = "#6b7280"; ctx.fillRect(-s * 0.6, -s * 0.3, s * 0.3, s * 0.6)
        break
      case "flint_shard":
        ctx.fillStyle = "#4b5563"; ctx.beginPath()
        ctx.moveTo(-s * 0.5, s * 0.5); ctx.lineTo(-s * 0.3, -s * 0.6); ctx.lineTo(s * 0.4, -s * 0.3); ctx.lineTo(s * 0.3, s * 0.6)
        ctx.closePath(); ctx.fill()
        break
      case "geode":
        ctx.fillStyle = "#6b7280"; ctx.beginPath(); ctx.arc(0, 0, s * 0.7, 0, Math.PI * 2); ctx.fill()
        ctx.fillStyle = "#a78bfa"; ctx.beginPath(); ctx.arc(s * 0.1, -s * 0.1, s * 0.3, 0, Math.PI * 2); ctx.fill()
        break
      case "ore_chunk":
        ctx.fillStyle = "#78716c"; ctx.beginPath()
        ctx.moveTo(-s * 0.5, s * 0.4); ctx.lineTo(-s * 0.4, -s * 0.4); ctx.lineTo(s * 0.3, -s * 0.5); ctx.lineTo(s * 0.5, s * 0.3)
        ctx.closePath(); ctx.fill()
        ctx.fillStyle = "#b08d57"; ctx.beginPath(); ctx.arc(s * 0.1, 0, s * 0.2, 0, Math.PI * 2); ctx.fill()
        break
      case "fossil":
        ctx.fillStyle = "#d6d3d1"; ctx.beginPath(); ctx.ellipse(0, 0, s * 0.6, s * 0.5, 0, 0, Math.PI * 2); ctx.fill()
        ctx.strokeStyle = "#a8a29e"; ctx.lineWidth = 1
        ctx.beginPath(); ctx.arc(0, 0, s * 0.3, 0.2, Math.PI * 1.5); ctx.stroke()
        break
      default:
        ctx.fillStyle = "#9ca3af"; ctx.fillRect(-s * 0.5, -s * 0.5, s, s)
    }
    ctx.restore()
  }
}
